import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Pill, Tablets, FlaskConical, Package } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { QuoteDialog } from "./QuoteDialog";
import { StickyMobileCTA } from "./StickyMobileCTA";
import { Toaster } from "../ui/sonner";
import {
  getServiceBySlug,
  SERVICES,
  SERVICE_QUOTE_NOTE,
} from "../../lib/services";

const ICONS = {
  capsules: Pill,
  tablets: Tablets,
  powders: FlaskConical,
  packaging: Package,
};

export const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const openQuote = () => setQuoteOpen(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = ICONS[service.slug];
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <div className="App bg-[#FAFAFA] text-[#1A1A1A] min-h-screen font-body pb-16 lg:pb-0 overflow-x-hidden">
      <Header onQuoteClick={openQuote} />

      <main>
        {/* Hero strip */}
        <section
          data-testid="service-detail-hero"
          className="relative pt-32 md:pt-40 pb-16 md:pb-20 bg-white border-b border-[#E5E5E5]"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Link
              to="/#services"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#D4AF37] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to capabilities
            </Link>

            <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="overline">
                  <span className="gold-divider mr-3 align-middle" />
                  Manufacturing capability
                </div>
                <div className="mt-6 flex items-center gap-4">
                  <span className="inline-flex items-center justify-center w-14 h-14 border border-[#D4AF37] text-[#D4AF37]">
                    <Icon className="w-6 h-6" strokeWidth={1.4} />
                  </span>
                  <h1
                    data-testid="service-title"
                    className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] leading-[1.05]"
                  >
                    {service.title}
                  </h1>
                </div>
                <p className="mt-6 text-base md:text-lg text-[#595959] leading-relaxed max-w-2xl">
                  {service.headline}
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <button
                  data-testid="service-detail-quote"
                  onClick={openQuote}
                  className="btn-gold rounded-sm h-12 px-7 text-sm font-semibold tracking-wider inline-flex items-center gap-2"
                >
                  Get a Quote <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {service.image && (
            <div className="max-w-7xl mx-auto px-6 md:px-10 mt-14">
              <div className="aspect-[21/9] md:aspect-[24/9] overflow-hidden border border-[#E5E5E5] relative">
                <img
                  src={service.image}
                  alt={`${service.title} manufacturing`}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          )}
        </section>
        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="overline">
                <span className="gold-divider mr-3 align-middle" />
                About this capability
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-6 text-[#1A1A1A] leading-tight">
                What we produce.
              </h2>
              <p className="text-[#595959] mt-5 leading-relaxed">
                {service.intro}
              </p>

              <ul className="mt-8 space-y-3">
                {service.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[#2A2A2A]"
                  >
                    <CheckCircle2
                      className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0"
                      strokeWidth={1.6}
                    />
                    <span className="leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              {service.note && (
                <div className="mb-6 border-l-2 border-[#D4AF37] pl-5 py-3 bg-white">
                  <div className="text-sm text-[#2A2A2A] leading-relaxed">
                    {service.note}
                  </div>
                </div>
              )}

              <div className="mt-0 p-6 md:p-7 bg-[#1A1A1A] text-white">
                <div className="overline" style={{ color: "#D4AF37" }}>
                  Note
                </div>
                <p
                  data-testid="service-quote-note"
                  className="text-white/80 mt-3 leading-relaxed text-sm md:text-base"
                >
                  {SERVICE_QUOTE_NOTE}
                </p>
                <button
                  data-testid="service-quote-bottom"
                  onClick={openQuote}
                  className="mt-5 inline-flex items-center gap-2 h-11 px-6 btn-gold rounded-sm text-sm font-semibold tracking-wider"
                >
                  Discuss your specs <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Other capabilities */}
        <section className="py-20 md:py-28 bg-white border-t border-[#E5E5E5]">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="overline">
              <span className="gold-divider mr-3 align-middle" />
              Other capabilities
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-6 text-[#1A1A1A]">
              Explore the rest of our line.
            </h2>

            <div className="mt-10 grid md:grid-cols-3 gap-4">
              {otherServices.map((s) => {
                const OIcon = ICONS[s.slug];
                return (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`other-service-${s.slug}`}
                    className="group bg-white border border-[#E5E5E5] p-7 transition-all hover:border-[#D4AF37] hover:-translate-y-0.5"
                  >
                    <div className="flex items-start justify-between">
                      <OIcon
                        className="w-6 h-6 text-[#D4AF37]"
                        strokeWidth={1.4}
                      />
                      <ArrowUpRight className="w-4 h-4 text-[#1A1A1A]/40 group-hover:text-[#D4AF37]" />
                    </div>
                    <div className="font-serif-display text-2xl text-[#1A1A1A] mt-5">
                      {s.title}
                    </div>
                    <p className="text-sm text-[#595959] mt-2 leading-relaxed">
                      {s.short}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyMobileCTA />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};
